import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Building2,
    Globe,
    KeyRound,
    Loader2,
    LogOut,
    Mail,
    Phone,
    RefreshCw,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
    ar: {
        adminTitle: "لوحة الإدارة",
        loginTitle: "دخول الإدارة",
        email: "البريد الإلكتروني",
        password: "كلمة المرور",
        login: "تسجيل الدخول",
        loggingIn: "جارٍ التحقق…",
        loginError: "بيانات الدخول غير صحيحة أو الحساب مقفل مؤقتًا",
        messages: "رسائل العملاء",
        logout: "تسجيل الخروج",
        refresh: "تحديث",
        empty: "لا توجد رسائل بعد",
        loading: "جارٍ التحميل…",
        notFound: "الصفحة غير موجودة",
        back: "العودة إلى الموقع",
        changePw: "تغيير كلمة المرور",
        currentPw: "كلمة المرور الحالية",
        newPw: "كلمة المرور الجديدة",
        confirmPw: "تأكيد كلمة المرور الجديدة",
        save: "حفظ كلمة المرور",
        saving: "جارٍ الحفظ…",
        cancel: "إلغاء",
        pwMismatch: "كلمتا المرور غير متطابقتين",
        pwShort: "يجب أن تتكون كلمة المرور الجديدة من 8 أحرف على الأقل",
        pwWrong: "كلمة المرور الحالية غير صحيحة",
        pwChanged: "تم تغيير كلمة المرور بنجاح",
        pwError: "تعذّر التغيير، حاول مرة أخرى",
    },
    en: {
        adminTitle: "Admin Panel",
        loginTitle: "Admin Login",
        email: "Email",
        password: "Password",
        login: "Sign in",
        loggingIn: "Checking…",
        loginError: "Wrong credentials or account temporarily locked",
        messages: "Customer Messages",
        logout: "Log out",
        refresh: "Refresh",
        empty: "No messages yet",
        loading: "Loading…",
        notFound: "Page not found",
        back: "Back to website",
        changePw: "Change Password",
        currentPw: "Current password",
        newPw: "New password",
        confirmPw: "Confirm new password",
        save: "Save password",
        saving: "Saving…",
        cancel: "Cancel",
        pwMismatch: "Passwords do not match",
        pwShort: "New password must be at least 8 characters",
        pwWrong: "Current password is incorrect",
        pwChanged: "Password changed successfully",
        pwError: "Could not change password, please try again",
    },
};

export default function AdminPage() {
    const { L, lang } = useLanguage();
    const navigate = useNavigate();
    const t = T[lang];

    const [user, setUser] = useState(undefined); // undefined = checking
    const [messages, setMessages] = useState([]);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [creds, setCreds] = useState({ email: "", password: "" });
    const [signingIn, setSigningIn] = useState(false);

    const [showPwForm, setShowPwForm] = useState(false);
    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [savingPw, setSavingPw] = useState(false);

    useEffect(() => {
        document.title = "Logistics Bridge — " + t.adminTitle;
    }, [lang, t.adminTitle]);

    const loadMessages = async () => {
        setLoadingMsgs(true);
        try {
            let res = await axios.get(`${API}/admin/messages`, { withCredentials: true });
            setMessages(res.data.messages || []);
        } catch (e) {
            if (e.response?.status === 401) {
                try {
                    await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
                    const res = await axios.get(`${API}/admin/messages`, { withCredentials: true });
                    setMessages(res.data.messages || []);
                } catch {
                    setUser(false);
                }
            }
        } finally {
            setLoadingMsgs(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                await axios.get(`${API}/auth/me`, { withCredentials: true });
                setUser(true);
                loadMessages();
            } catch {
                setUser(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (e) => {
        e.preventDefault();
        setSigningIn(true);
        try {
            await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
            setUser(true);
            loadMessages();
        } catch {
            toast.error(t.loginError);
        } finally {
            setSigningIn(false);
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch {
            /* ignore */
        }
        setUser(false);
        setMessages([]);
    };

    const changePassword = async (e) => {
        e.preventDefault();
        if (pw.next.length < 8) {
            toast.error(t.pwShort);
            return;
        }
        if (pw.next !== pw.confirm) {
            toast.error(t.pwMismatch);
            return;
        }
        setSavingPw(true);
        try {
            await axios.post(
                `${API}/auth/change-password`,
                { current_password: pw.current, new_password: pw.next },
                { withCredentials: true }
            );
            toast.success(t.pwChanged);
            setPw({ current: "", next: "", confirm: "" });
            setShowPwForm(false);
        } catch (err) {
            if (err.response?.status === 401) toast.error(t.pwWrong);
            else if (err.response?.status === 400) toast.error(t.pwShort);
            else toast.error(t.pwError);
        } finally {
            setSavingPw(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-gold-line bg-ink/60 px-4 py-3 text-sm text-txt-1 outline-none transition-colors focus:border-gold-line-strong";

    if (user === undefined) {
        return (
            <div className="grid min-h-screen place-items-center bg-ink text-brass">
                <Loader2 className="size-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ink px-6 py-10 text-txt-1" dir="auto">
            <div className="mx-auto max-w-5xl">
                <header className="flex flex-wrap items-center justify-between gap-4 border-b border-gold-line/40 pb-6">
                    <div className="flex items-center gap-3">
                        <img src="/logo.jpg" alt="X New Line" className="size-10 rounded-lg object-cover" />
                        <div>
                            <p className="font-display text-lg font-bold text-brass-bright">{t.adminTitle}</p>
                            <p className="font-mono-x text-[10px] tracking-[0.3em] text-txt-3">LOGISTICS BRIDGE</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={() => setShowPwForm(!showPwForm)}
                            data-testid="admin-change-password-button"
                            className={`flex items-center gap-2 rounded-full border px-5 py-2 text-xs font-semibold transition-colors ${
                                showPwForm
                                    ? "border-brass bg-brass/10 text-brass-bright"
                                    : "border-gold-line-strong text-brass hover:bg-brass/10"
                            }`}
                        >
                            <KeyRound className="size-3.5" />
                            {t.changePw}
                        </button>
                        <button
                            onClick={() => navigate("/")}
                            data-testid="admin-back-to-site"
                            className="rounded-full border border-gold-line-strong px-5 py-2 text-xs font-semibold text-brass transition-colors hover:bg-brass/10"
                        >
                            {t.back}
                        </button>
                        {user && (
                            <button
                                onClick={logout}
                                data-testid="admin-logout-button"
                                className="flex items-center gap-2 rounded-full bg-brass px-5 py-2 text-xs font-bold text-ink transition-transform hover:scale-105"
                            >
                                <LogOut className="size-3.5" />
                                {t.logout}
                            </button>
                        )}
                    </div>
                </header>

                {user === false && (
                    <form
                        onSubmit={login}
                        data-testid="admin-login-form"
                        className="glass mx-auto mt-16 max-w-md rounded-2xl border border-gold-line p-8"
                    >
                        <h1 className="font-display text-2xl font-bold text-txt-1">{t.loginTitle}</h1>
                        <label className="mt-6 mb-2 block text-sm text-txt-2">{t.email}</label>
                        <input
                            type="email"
                            required
                            dir="ltr"
                            value={creds.email}
                            onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                            data-testid="admin-email-input"
                            className={inputCls}
                        />
                        <label className="mt-4 mb-2 block text-sm text-txt-2">{t.password}</label>
                        <input
                            type="password"
                            required
                            dir="ltr"
                            value={creds.password}
                            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                            data-testid="admin-password-input"
                            className={inputCls}
                        />
                        <button
                            type="submit"
                            disabled={signingIn}
                            data-testid="admin-login-button"
                            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brass px-6 py-3 text-sm font-bold text-ink transition-transform hover:scale-[1.02] disabled:opacity-60"
                        >
                            {signingIn ? (
                                <>
                                    <Loader2 className="size-4 animate-spin" />
                                    {t.loggingIn}
                                </>
                            ) : (
                                t.login
                            )}
                        </button>
                    </form>
                )}

                {user && showPwForm && (
                    <form
                        onSubmit={changePassword}
                        data-testid="admin-change-password-form"
                        className="glass mt-8 rounded-2xl border border-gold-line p-8"
                    >
                        <h2 className="font-display text-lg font-bold text-txt-1">{t.changePw}</h2>
                        <div className="mt-5 grid gap-5 sm:grid-cols-3">
                            <div>
                                <label className="mb-2 block text-sm text-txt-2">{t.currentPw}</label>
                                <input
                                    type="password"
                                    required
                                    dir="ltr"
                                    value={pw.current}
                                    onChange={(e) => setPw({ ...pw, current: e.target.value })}
                                    data-testid="admin-current-password-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm text-txt-2">{t.newPw}</label>
                                <input
                                    type="password"
                                    required
                                    dir="ltr"
                                    value={pw.next}
                                    onChange={(e) => setPw({ ...pw, next: e.target.value })}
                                    data-testid="admin-new-password-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm text-txt-2">{t.confirmPw}</label>
                                <input
                                    type="password"
                                    required
                                    dir="ltr"
                                    value={pw.confirm}
                                    onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                                    data-testid="admin-confirm-password-input"
                                    className={inputCls}
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                type="submit"
                                disabled={savingPw}
                                data-testid="admin-change-password-submit"
                                className="flex items-center gap-2 rounded-full bg-brass px-6 py-3 text-sm font-bold text-ink transition-transform hover:scale-[1.02] disabled:opacity-60"
                            >
                                {savingPw ? (
                                    <>
                                        <Loader2 className="size-4 animate-spin" />
                                        {t.saving}
                                    </>
                                ) : (
                                    t.save
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowPwForm(false)}
                                data-testid="admin-change-password-cancel"
                                className="rounded-full border border-gold-line-strong px-6 py-3 text-sm font-semibold text-brass transition-colors hover:bg-brass/10"
                            >
                                {t.cancel}
                            </button>
                        </div>
                    </form>
                )}

                {user && (
                    <section className="mt-10">
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-xl font-bold text-txt-1" data-testid="admin-messages-title">
                                {t.messages}{" "}
                                <span className="font-mono-x text-sm text-brass">({messages.length})</span>
                            </h2>
                            <button
                                onClick={loadMessages}
                                data-testid="admin-refresh-button"
                                className="flex items-center gap-2 rounded-full border border-gold-line-strong px-4 py-2 text-xs font-semibold text-brass transition-colors hover:bg-brass/10"
                            >
                                <RefreshCw className={`size-3.5 ${loadingMsgs ? "animate-spin" : ""}`} />
                                {t.refresh}
                            </button>
                        </div>

                        {loadingMsgs && messages.length === 0 ? (
                            <p className="mt-10 text-center text-txt-3">{t.loading}</p>
                        ) : messages.length === 0 ? (
                            <p className="mt-10 text-center text-txt-3" data-testid="admin-empty-state">
                                {t.empty}
                            </p>
                        ) : (
                            <ul className="mt-8 space-y-4">
                                {messages.map((m, i) => (
                                    <li
                                        key={m.id}
                                        data-testid={`admin-message-row-${i + 1}`}
                                        className="glass rounded-2xl border border-gold-line p-6"
                                    >
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <p className="font-display text-lg font-bold text-brass-bright" dir="auto">
                                                {m.name}
                                            </p>
                                            <p className="font-mono-x text-[10px] text-txt-3" dir="ltr">
                                                {m.created_at ? new Date(m.created_at).toLocaleString(lang === "ar" ? "ar" : "en-GB") : ""}
                                            </p>
                                        </div>
                                        {m.service && (
                                            <span className="mt-3 inline-block rounded-full border border-gold-line-strong bg-brass/10 px-4 py-1 text-xs font-semibold text-brass-bright" dir="auto">
                                                {m.service}
                                            </span>
                                        )}
                                        <ul className="mt-4 space-y-2 text-sm">
                                            {m.company && (
                                                <li className="flex items-center gap-2">
                                                    <Building2 className="size-3.5 shrink-0 text-brass" />
                                                    <span dir="auto" className="text-txt-2">{m.company}</span>
                                                </li>
                                            )}
                                            {m.country && (
                                                <li className="flex items-center gap-2">
                                                    <Globe className="size-3.5 shrink-0 text-brass" />
                                                    <span dir="auto" className="text-txt-2">{m.country}</span>
                                                </li>
                                            )}
                                            <li className="flex items-center gap-2">
                                                <Phone className="size-3.5 shrink-0 text-brass" />
                                                <a href={`tel:${m.phone.replace(/[^+\d]/g, "")}`} dir="ltr" className="text-txt-2 transition-colors hover:text-brass">
                                                    {m.phone}
                                                </a>
                                            </li>
                                            {m.email && (
                                                <li className="flex items-center gap-2">
                                                    <Mail className="size-3.5 shrink-0 text-brass" />
                                                    <a href={`mailto:${m.email}`} dir="ltr" className="text-txt-2 transition-colors hover:text-brass">
                                                        {m.email}
                                                    </a>
                                                </li>
                                            )}
                                        </ul>
                                        <p className="mt-4 border-t border-gold-line/40 pt-4 text-sm leading-relaxed text-txt-2" dir="auto">
                                            {m.message}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}
